import Alpine, { AlpineComponent } from "alpinejs";
import {
	BasketRowViewModel,
	BasketService,
	BasketViewModel,
	CreateOrderParameters,
	GoogleService,
	OrderService,
} from "../api";
import { useTranslations } from "../hooks/useTranslations";
import { MiniCartStore } from "./store/miniCartStore";

type Checkout = AlpineComponent<{
	company: string;
	firstName: string;
	lastName: string;
	email: string;
	address: string;
	phone: string;
	city: string;
	postalCode: string;
	addressOther: string;
	cityOther: string;
	postalCodeOther: string;
	orderTag: string;
	desiredShippingDate: Date | null;
	otherDeliveryAddress: boolean;
	message: string;
	submissionErrors: string[];
	isLoading: boolean;
	isLoadingBasket: boolean;
	basket: BasketViewModel | undefined;
	removeFromBasket: (lineNo: number) => void;
	incrementBasketItem: (basketItem: BasketRowViewModel) => Promise<void>;
	decrementBasketItem: (basketItem: BasketRowViewModel) => Promise<void>;
	updateBasketItem: (basketItem: BasketRowViewModel) => Promise<void>;
	submitOrder: () => void;
	checkInStockItems: () => void;
}>;

interface FormData {
	companyName?: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	address?: string;
	phone?: string;
	city?: string;
	postalCode?: string;
}

export default (initialBasket: BasketViewModel | undefined, initialForm: FormData): Checkout => {
	const t = useTranslations();
	return {
		company: initialForm.companyName || "",
		firstName: initialForm.firstName || "",
		lastName: initialForm.lastName || "",
		email: initialForm.email || "",
		address: initialForm.address || "",
		city: initialForm.city || "",
		postalCode: initialForm.postalCode || "",
		phone: initialForm.phone || "",
		addressOther: "",
		cityOther: "",
		postalCodeOther: "",
		orderTag: "",
		message: "",
		otherDeliveryAddress: false,
		desiredShippingDate: null,
		basket: initialBasket,
		submissionErrors: [],
		isLoading: false,
		isLoadingBasket: false,

		removeFromBasket(lineNo: number) {
			if (!this.basket) {
				console.error("No basket available to remove item from.");
				return;
			}
			BasketService.removeFromBasket({ body: { lineNo: lineNo, basketId: this.basket.id } }).then((response) => {
				this.basket = response.data;
				let numberOfItems = 0;
				response.data?.items.forEach((item) => {
					if (item.hasMLEUnit) {
						numberOfItems += item.quantity / item.mleFactor;
					} else {
						numberOfItems += item.quantity;
					}
				});
				(Alpine.store("cart") as MiniCartStore).setItemCount(numberOfItems, false);
				this.checkInStockItems();
			});
		},
		async incrementBasketItem(basketItem: BasketRowViewModel) {
			if (basketItem.hasMLEUnit) {
				basketItem.quantity = Math.round((basketItem.quantity + basketItem.mleFactor) * 100) / 100;
			} else {
				basketItem.quantity += 1;
			}
			await this.updateBasketItem(basketItem);
		},
		async decrementBasketItem(basketItem: BasketRowViewModel) {
			if (basketItem.hasMLEUnit) {
				if (basketItem.quantity > basketItem.mleFactor) {
					basketItem.quantity = Math.round((basketItem.quantity - basketItem.mleFactor) * 100) / 100;
					await this.updateBasketItem(basketItem);
				}
			} else if (basketItem.quantity > 1) {
				basketItem.quantity -= 1;
				await this.updateBasketItem(basketItem);
			}
		},
		async updateBasketItem(basketItem: BasketRowViewModel) {
			if (!this.basket) {
				console.error("No basket available to update item in.");
				return;
			}

			if (!basketItem.hasMLEUnit) {
				if (basketItem.quantity < 1 || isNaN(basketItem.quantity)) {
					basketItem.quantity = 1;
				} else {
					basketItem.quantity = Math.floor(basketItem.quantity);
				}
			}

			const exceedsStock = basketItem.quantity > basketItem.numberInStock;
			let showQuantityNotification = false;
			let quantityNotification = "";

			if (exceedsStock && this.basket.articleStatuses.expiringOrder === basketItem.articleStatus) {
				basketItem.quantity = basketItem.numberInStock;
				quantityNotification = t["checkout.AboveMaxStockQuantityNotificationExpiring"];
				showQuantityNotification = true;
			}

			if (basketItem.quantity > 9999) {
				basketItem.quantity = 9999;
			}

			this.isLoadingBasket = true;

			await BasketService.updateBasketItem({
				body: {
					lineNo: basketItem.lineNo,
					quantity: basketItem.quantity,
					basketId: this.basket.id,
					partNo: basketItem.partNo,
				},
			})
				.then((response) => {
					this.basket = response.data;
					let numberOfItems = 0;
					response.data?.items.forEach((item) => {
						if (item.hasMLEUnit) {
							numberOfItems += item.quantity / item.mleFactor;
						} else {
							numberOfItems += item.quantity;
						}
					});

					(Alpine.store("cart") as MiniCartStore).setItemCount(numberOfItems, false);
					const newBasketItem = this.basket?.items.find((item) => item.lineNo === basketItem.lineNo);
					if (!newBasketItem) {
						return;
					}
					newBasketItem.showQuantityNotification = showQuantityNotification;
					newBasketItem.quantityNotification = quantityNotification;

					this.checkInStockItems();
					setTimeout(async () => {
						const googleTracking = await GoogleService.addToCart({
							body: { partNo: basketItem.partNo, quantity: basketItem.quantity, priceListId: 0 },
						});
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						(window as any).dataLayer.push({ ecommerce: null });
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						(window as any).dataLayer.push({
							event: "add_to_cart",
							ecommerce: googleTracking.data,
						});
					}, 100);
				})
				.finally(() => {
					this.isLoadingBasket = false;
				});
		},
		checkInStockItems() {
			if (!this.basket) {
				return;
			}
			for (const item of this.basket.items) {
				if (item.articleStatus === this.basket.articleStatuses.stockOrder && item.quantity > item.numberInStock) {
					item.showQuantityNotification = true;
					item.quantityNotification = t["checkout.AboveMaxStockQuantityNotificationInStock"];
				}
				if (item.articleStatus === this.basket.articleStatuses.madeToOrder && item.quantity > item.numberInStock) {
					item.showQuantityNotification = true;
					item.quantityNotification = t["checkout.AboveMaxStockQuantityNotificationTemporaryInStock"];
				}
			}
		},
		async submitOrder() {
			const orderData: CreateOrderParameters = {
				company: this.company,
				firstName: this.firstName,
				lastName: this.lastName,
				email: this.email,
				address: this.otherDeliveryAddress ? this.addressOther : this.address,
				city: this.otherDeliveryAddress ? this.cityOther : this.city,
				postalCode: this.otherDeliveryAddress ? this.postalCodeOther : this.postalCode,
				phone: this.phone,
				orderTag: this.orderTag,
				desiredShippingDate: this.desiredShippingDate,
				message: this.message,
			};
			this.isLoading = true;

			try {
				const { response, data, error } = await OrderService.create({ body: orderData });

				if (response?.status === 200 && !error) {
					location.href = data as string;
					return;
				}

				if (Array.isArray(error)) {
					for (const e of error) {
						this.submissionErrors.push(e.errorMessage ?? "Unknown error");
					}
				} else if (typeof error === "string") {
					console.error("Error submitting order:", error);
					this.submissionErrors.push(error);
				} else {
					console.error("Unknown error submitting order:", error);
					this.submissionErrors.push("Unknown error");
				}
			} catch (err) {
				console.error("Error submitting order:", err);
				this.submissionErrors.push("Unknown error");
			} finally {
				setTimeout(() => {
					this.isLoading = false;
				}, 3000);
			}
		},
	};
};
